import { useRouter } from 'next/router';
import { UserPlanType } from '@creditflowresearch/user-plan/UserPlan';
import PageMeta from '@/creditflowresearch/ui/common/components/PageMeta';
import metaTags from '@/creditflowresearch/ui/common/metaTagsConstants';
import ProductPageHeader from '@/creditflowresearch/ui/common/components/templates/ProductPageHeader';
import { useProductGradient } from '@/creditflowresearch/ui/common/hooks/products/useProductGradient';
import { useUserPlanContext } from '@/creditflowresearch/application/User/UserPlan/UserPlanContext';
import { usePageFiltersContext } from '@/creditflowresearch/application/PageFilters/PageFiltersContext';
import { useCorporateBondScreenerFilters } from '@/creditflowresearch/application/PageFilters/hooks';
import { BondscreenerNoAccessComponent } from '@/creditflowresearch/ui/components/bondscreener/BondscreenerNoAccess';
import CheckUserAccess from '@/creditflowresearch/ui/common/components/CheckUserAccess';
import { useBreadcrumbs } from '@/creditflowresearch/ui/common/hooks';
import getBondscreenerBreadcrumbs from '@/creditflowresearch/ui/components/bondscreener/getBondscreenerBreadcrumbs';
import { CorporateBondScreenerMySelectionContextProvider } from '@/creditflowresearch/application/CorporateBondScreener/CorporateBondScreenerMySelectionContext';
import { CorporateBondScreenerContextProvider } from '@/creditflowresearch/application/CorporateBondScreener/CorporateBondScreenerContext';
import {
  CorporateBondScreenerDictionariesProvider,
} from '@/creditflowresearch/application/CorporateBondScreener/CorporateBondScreenerDictionariesContext';
import {
  BondScreenerColumnsProvider,
  useBondScreenerColumnsContext,
} from '@/creditflowresearch/application/BondScreener/BondScreenerColumnsContext';
import MissingSomethingModalButton from '@/creditflowresearch/ui/common/components/MissingSomethingModalButton';
import type { IUseCorporateBondScreenerDictionariesProps } from '@/creditflowresearch/application/CorporateBondScreener/useCorporateBondScreenerDictionaries';
import type { ICorporateBondScreenerTrancheCollectionResponse } from '@/creditflowresearch/domain/vo/Bondscreener/ICorporateBondScreenerTranche';
import type { TFilterPayload } from '@/creditflowresearch/domain/vo/Bondscreener';
import CorporateBondScreenerView
  from '@/creditflowresearch/ui/components/corporate-bond-screener/CorporateBondScreenerView';
import type { IBondScreenerConfig } from '@/creditflowresearch/domain/vo/Bondscreener/IBondScreenerConfig';
import type { IBondScreenerViewsResponse } from '@/creditflowresearch/domain/vo/Bondscreener/IBondScreenerView';
import { useDictionariesContext } from '@/creditflowresearch/application/Dictionaries/v2/DictionariesContext';
import Market from '@/creditflowresearch/domain/Market';
import Segment from '@/creditflowresearch/domain/Segment';
import Product from '@/creditflowresearch/domain/Product';
import { Tile } from '@creditflowresearch/uilib/Tile';
import { useAccessGrant } from '@/creditflowresearch/application/User/access/grants/useAccessGrant';

import { getCorporateBondScreenerMetaSchema } from './getCorporateBondScreenerMetaSchema';

import styles from '@/creditflowresearch/ui/components/bondscreener/bondscreener.module.scss';

interface ICorporateBondScreenerPageProps {
  initialTranches?: ICorporateBondScreenerTrancheCollectionResponse,
  bondScreenerConfig: IBondScreenerConfig<TFilterPayload>,
  bondScreenerViews: IBondScreenerViewsResponse<TFilterPayload>,
  initialActiveColumnsIds: string[],
  initialFilters: TFilterPayload,
}

const CorporateBondScreenerPage = ({
  initialTranches,
  bondScreenerConfig,
  bondScreenerViews,
  initialActiveColumnsIds,
  initialFilters,
}: ICorporateBondScreenerPageProps) => {
  const { query } = useRouter();
  const panelBackground = useProductGradient();
  const { checkAccessByUserPlan } = useUserPlanContext();
  const {
    checkAccessByUserMarket,
    checkAccessByUserProducts,
    corporateProductFilters,
  } = usePageFiltersContext();
  const bondScreenerFilters = useCorporateBondScreenerFilters();

  const {
    currencies, benchmarks, couponTypes, rankings, countries, industries, dealStatuses,
    esgTypes, listings, uopTypes, debutTypes, registration, sizeDescriptions,
  } = useDictionariesContext();

  const completedDictionaries: IUseCorporateBondScreenerDictionariesProps = {
    currencies: currencies.getAll() as IUseCorporateBondScreenerDictionariesProps['currencies'],
    benchmarks: benchmarks.getAll() as IUseCorporateBondScreenerDictionariesProps['benchmarks'],
    couponTypes: couponTypes.getAll() as IUseCorporateBondScreenerDictionariesProps['couponTypes'],
    rankings: rankings.getAll() as IUseCorporateBondScreenerDictionariesProps['rankings'],
    countries: countries.getAll() as IUseCorporateBondScreenerDictionariesProps['countries'],
    industries: industries.getAll() as IUseCorporateBondScreenerDictionariesProps['industries'],
    dealStatuses: dealStatuses.getAll() as IUseCorporateBondScreenerDictionariesProps['dealStatuses'],
    esgTypes: esgTypes.getAll() as IUseCorporateBondScreenerDictionariesProps['esgTypes'],
    listings: listings.getAll() as IUseCorporateBondScreenerDictionariesProps['listings'],
    uopTypes: uopTypes.getAll() as IUseCorporateBondScreenerDictionariesProps['uopTypes'],
    debutTypes: debutTypes.getAll() as IUseCorporateBondScreenerDictionariesProps['debutTypes'],
    registrations: registration.getAll() as IUseCorporateBondScreenerDictionariesProps['registrations'],
    sizeDescriptions: sizeDescriptions.getAll() as IUseCorporateBondScreenerDictionariesProps['sizeDescriptions'],
    markets: Market.getCollection(),
    marketSegments: Segment.getCollection(),
    products: Product.getCollection(),
  };

  useBreadcrumbs(
    () => getBondscreenerBreadcrumbs(query),
    [query],
  );

  const granted = useAccessGrant();

  const checks = [
    granted || checkAccessByUserPlan(UserPlanType.PRO),
    granted || checkAccessByUserMarket(),
    granted || checkAccessByUserProducts(),
  ];

  const { title, description } = metaTags.getCorporateBondscreener();

  return (
    <>
      <PageMeta title={ title } description={ description } schema={ getCorporateBondScreenerMetaSchema() } />
      <ProductPageHeader
        title="Bond Screener"
        items={ [
          ...bondScreenerFilters,
          { key: 'products', items: corporateProductFilters },
        ] }
        blockingAccessConditions={ [{ checks: [granted || checkAccessByUserPlan(UserPlanType.STARTER)] }] }
        accessConditions={ [{ checks }] }
        panelBackground={ panelBackground }
      />
      <Tile className={ styles.cardWrapper } dataTest="corporateBondScreenerTile">
        <CheckUserAccess conditions={ [{ checks, fallback: BondscreenerNoAccessComponent }] }>
          <BondScreenerColumnsProvider
            config={ bondScreenerConfig }
            initialActiveColumnsIds={ initialActiveColumnsIds }
          >
            <CorporateBondScreenerDictionariesProvider dictionaries={ completedDictionaries }>
              <CorporateBondScreenerMySelectionContextProvider>
                <CorporateBondScreenerContent
                  initialTranches={ initialTranches }
                  initialFilters={ initialFilters }
                  bondScreenerViews={ bondScreenerViews }
                />
              </CorporateBondScreenerMySelectionContextProvider>
            </CorporateBondScreenerDictionariesProvider>
          </BondScreenerColumnsProvider>
        </CheckUserAccess>
      </Tile>
      <MissingSomethingModalButton />
    </>
  );
};

interface ICorporateBondScreenerContentProps {
  initialTranches?: ICorporateBondScreenerTrancheCollectionResponse,
  initialFilters: TFilterPayload,
  bondScreenerViews: IBondScreenerViewsResponse<TFilterPayload>,
}

const CorporateBondScreenerContent = ({
  initialTranches,
  initialFilters,
  bondScreenerViews,
}: ICorporateBondScreenerContentProps) => {
  const { activeColumnsIds } = useBondScreenerColumnsContext();

  return (
    <CorporateBondScreenerContextProvider
      initialTranches={ initialTranches }
      columns={ activeColumnsIds }
      initialFilters={ initialFilters }
    >
      <CorporateBondScreenerView initialViews={ bondScreenerViews } />
    </CorporateBondScreenerContextProvider>
  );
};

export default CorporateBondScreenerPage;
